import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { TrendingUp, TrendingDown, Zap, Clock, Target, Search, FileText, BarChart3, Activity, Users, Loader2, DollarSign } from 'lucide-react';
import { HistoriqueService } from '../services/HistoriqueService';

const DashboardHistorique = () => {
    const [periode, setPeriode] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // États pour les différentes données
    const [overview, setOverview] = useState(null);
    const [dailyStats, setDailyStats] = useState([]);
    const [topKeywords, setTopKeywords] = useState([]);
    const [topUsers, setTopUsers] = useState([]);
    const [recentActivity, setRecentActivity] = useState([]);
    const [typeDistribution, setTypeDistribution] = useState([]);

    const COLORS = ['#f97316', '#fb923c', '#fdba74', '#fed7aa', '#ffedd5', '#fff7ed', '#ea580c'];

    // Charger toutes les données
    useEffect(() => {
        const controller = new AbortController();

        const fetchAllData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Calculer les dates de filtre selon la période
                const filters = {};
                const now = new Date();

                if (periode === 'today') {
                    filters.start_date = now.toISOString().split('T')[0];
                    filters.end_date = now.toISOString().split('T')[0];
                } else if (periode === 'week') {
                    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                    filters.start_date = weekAgo.toISOString().split('T')[0];
                    filters.end_date = now.toISOString().split('T')[0];
                } else if (periode === 'month') {
                    const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                    filters.start_date = monthAgo.toISOString().split('T')[0];
                    filters.end_date = now.toISOString().split('T')[0];
                }

                // Charger toutes les statistiques en parallèle
                const [
                    overviewData,
                    dailyData,
                    keywordsData,
                    usersData,
                    activityData,
                    distributionData
                ] = await Promise.all([
                    HistoriqueService.getStatsOverview(filters, controller.signal),
                    HistoriqueService.getStatsDaily(7, filters, controller.signal),
                    HistoriqueService.getTopKeywords(5, filters, controller.signal),
                    HistoriqueService.getTopUsers(5, filters, controller.signal),
                    HistoriqueService.getRecentActivity(5, filters, controller.signal),
                    HistoriqueService.getTypeDistribution(filters, controller.signal)
                ]);

                setOverview(overviewData);
                setDailyStats(dailyData || []);
                setTopKeywords(keywordsData || []);
                setTopUsers(usersData || []);
                setRecentActivity(activityData || []);
                setTypeDistribution(distributionData || []);

            } catch (err) {
                if (err.name !== 'AbortError') {
                    setError('Erreur lors du chargement des données');
                    console.error('Error fetching dashboard data:', err);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();

        return () => controller.abort();
    }, [periode]);

    // Préparer les données pour le graphique d'évolution
    const dataTotal = dailyStats.map(stat => ({
        day: new Date(stat.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        total: stat.total_searches || 0,
        success: stat.successful_searches || 0,
        failed: stat.failed_searches || 0
    }));

    // Préparer les données pour le graphique en donut (exclure les null)
    const pieData = typeDistribution
        .filter(item => item.type !== null)
        .map(item => ({
            name: item.type_display || item.type,
            value: item.percentage,
            count: item.count
        }));

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000 / 60); // différence en minutes

        if (diff < 1) return "À l'instant";
        if (diff < 60) return `${diff} min`;
        if (diff < 1440) return `${Math.floor(diff / 60)}h`;
        return date.toLocaleDateString('fr-FR');
    };

    const getTypeColor = (type) => {
        const colors = {
            'search': 'bg-blue-100 text-blue-700',
            'recherche_detaillee': 'bg-purple-100 text-purple-700',
            'decision_justice': 'bg-green-100 text-green-700',
            'api': 'bg-orange-100 text-orange-700',
            'chatbot': 'bg-pink-100 text-pink-700',
            'notification': 'bg-yellow-100 text-yellow-700'
        };
        return colors[type] || 'bg-gray-100 text-gray-700';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="animate-spin text-orange-500 mx-auto mb-4" size={64} />
                    <p className="text-gray-600">Chargement des statistiques...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                        <p className="text-red-700">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            {/* En-tête */}
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-orange-500 mb-2">Dashboard Global</h1>
                <p className="text-gray-600">Vue d'ensemble des statistiques de recherche</p>
            </div>

            {/* Filtre */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <div className="flex items-center gap-3 mb-4">
                    <BarChart3 className="text-orange-500" size={24} />
                    <h2 className="text-xl font-semibold text-orange-500">Filtrer les métriques par période</h2>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Période</label>
                    <select
                        value={periode}
                        onChange={(e) => setPeriode(e.target.value)}
                        className="w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                        <option value="all">Toutes les périodes</option>
                        <option value="today">Aujourd'hui</option>
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                    </select>
                </div>
            </div>

            {/* Statistiques principales */}
            {overview && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    {/* Total Recherches */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-600 text-sm">Total Recherches</span>
                            <div className="bg-orange-100 p-2 rounded-lg">
                                <Search className="text-orange-500" size={20} />
                            </div>
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mb-2">{overview.total_searches?.toLocaleString()}</div>
                        <div className="text-sm text-gray-600">Toutes périodes confondues</div>
                    </div>

                    {/* Crédits Utilisés */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-600 text-sm">Crédits Utilisés</span>
                            <div className="bg-blue-100 p-2 rounded-lg">
                                <DollarSign className="text-blue-500" size={20} />
                            </div>
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mb-2">{overview.total_credits?.toLocaleString()}</div>
                        <div className="text-sm text-gray-600">Moyenne: {overview.avg_credits_per_search?.toFixed(2)} / recherche</div>
                    </div>

                    {/* Taux de Succès */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-600 text-sm">Taux de Succès</span>
                            <div className="bg-green-100 p-2 rounded-lg">
                                <Target className="text-green-500" size={20} />
                            </div>
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mb-2">{overview.success_rate?.toFixed(1)}%</div>
                        <div className="text-sm text-green-600">{overview.successful_searches?.toLocaleString()} recherches réussies</div>
                    </div>

                    {/* Échecs */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-gray-600 text-sm">Échecs</span>
                            <div className="bg-red-100 p-2 rounded-lg">
                                <Activity className="text-red-500" size={20} />
                            </div>
                        </div>
                        <div className="text-4xl font-bold text-gray-900 mb-2">{overview.failed_searches?.toLocaleString()}</div>
                        <div className="text-sm text-red-600">{(100 - overview.success_rate)?.toFixed(1)}% d'échec</div>
                    </div>
                </div>
            )}

            {/* Graphiques principaux */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Évolution des recherches */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <BarChart3 className="text-orange-500" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Évolution des recherches (7 derniers jours)</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={dataTotal}>
                            <XAxis dataKey="day" stroke="#9ca3af" />
                            <YAxis stroke="#9ca3af" />
                            <Tooltip />
                            <Bar dataKey="success" fill="#10b981" name="Réussies" />
                            <Bar dataKey="failed" fill="#ef4444" name="Échouées" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Répartition par Type */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Activity className="text-orange-500" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Répartition par Type</h3>
                    </div>
                    {pieData.length > 0 && (
                        <>
                            <ResponsiveContainer width="100%" height={250}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={90}
                                        paddingAngle={2}
                                        dataKey="value"
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => `${value.toFixed(1)}%`} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="grid grid-cols-2 gap-2 mt-4">
                                {pieData.map((item, index) => (
                                    <div key={index} className="flex items-center gap-2">
                                        <div className="w-3 h-3 rounded" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                                        <span className="text-xs text-gray-600">{item.name} ({item.value.toFixed(1)}%)</span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Section inférieure */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top 5 Mots-clés */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Search className="text-orange-500" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Top 5 Mots-clés</h3>
                    </div>
                    <div className="space-y-3">
                        {topKeywords.map((item, index) => (
                            <div key={index} className="border-l-4 border-orange-500 pl-3 py-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-gray-800">{item.keyword}</span>
                                    <span className="text-sm bg-orange-100 text-orange-600 px-2 py-1 rounded">#{index + 1}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>{item.count} recherches</span>
                                    <span className="text-green-600">{item.success_rate?.toFixed(1)}% succès</span>
                                </div>
                            </div>
                        ))}
                        {topKeywords.length === 0 && (
                            <p className="text-gray-500 text-sm text-center py-4">Aucune donnée disponible</p>
                        )}
                    </div>
                </div>

                {/* Top 5 Utilisateurs */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Users className="text-orange-500" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Top 5 Utilisateurs</h3>
                    </div>
                    <div className="space-y-3">
                        {topUsers.map((item, index) => (
                            <div key={index} className="border-l-4 border-blue-500 pl-3 py-2">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="font-medium text-gray-800 text-sm">{item.user_name}</span>
                                    <span className="text-sm bg-blue-100 text-blue-600 px-2 py-1 rounded">#{index + 1}</span>
                                </div>
                                <div className="text-xs text-gray-500 mb-1">{item.user_email}</div>
                                <div className="flex items-center justify-between text-xs text-gray-600">
                                    <span>{item.total_searches} recherches</span>
                                    <span className="text-red-600">{item.failed_searches} échecs</span>
                                </div>
                            </div>
                        ))}
                        {topUsers.length === 0 && (
                            <p className="text-gray-500 text-sm text-center py-4">Aucune donnée disponible</p>
                        )}
                    </div>
                </div>

                {/* Activité Récente */}
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="flex items-center gap-3 mb-4">
                        <Clock className="text-orange-500" size={20} />
                        <h3 className="text-lg font-semibold text-gray-800">Activité Récente</h3>
                    </div>
                    <div className="space-y-3">
                        {recentActivity.map((item, index) => (
                            <div key={index} className="flex items-start gap-3 pb-3 border-b border-gray-100 last:border-0">
                                <div className={`p-2 rounded-lg mt-1 ${item.success ? 'bg-green-100' : 'bg-red-100'}`}>
                                    <Search className={item.success ? 'text-green-500' : 'text-red-500'} size={16} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate">{item.request}</div>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                                        <span className={`text-xs px-2 py-0.5 rounded ${getTypeColor(item.type)}`}>
                                            {item.type}
                                        </span>
                                        <span className="text-xs text-gray-500">{item.user_name}</span>
                                    </div>
                                    <div className="text-xs text-gray-400 mt-1">{formatDate(item.created_at)}</div>
                                </div>
                            </div>
                        ))}
                        {recentActivity.length === 0 && (
                            <p className="text-gray-500 text-sm text-center py-4">Aucune activité récente</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardHistorique;